/**
 * Email Settings Page
 * Configure SMTP settings and notification toggles (internal/external)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Email as EmailIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
} from '@mui/icons-material';
import { apiGet, apiPut, apiPost, extractApiError } from '../../utils/apiClient';
import logger from '../../utils/logger';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { groupByCategory } from '../../utils/notificationCategories';

// Scheduled digest types that support a configurable cadence (frequency + day).
const SCHEDULED_TYPES = new Set(['maturity_reminder', 'payment_reminder', 'overdue_payment']);
const FREQUENCY_OPTIONS = ['daily', 'weekly', 'monthly', 'never'];
const DAY_OF_WEEK_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function EmailSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [settings, setSettings] = useState({});
  // Notification types are fetched from the API (single source of truth) so the UI
  // never drifts from the backend registry. All types are internal-only.
  const [notificationTypes, setNotificationTypes] = useState([]);
  // Per-type cadence config for scheduled digests, keyed by notification_type.
  const [notificationConfig, setNotificationConfig] = useState({});
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, typesRes, configRes] = await Promise.all([
        apiGet('/email/settings'),
        apiGet('/email/notification-types'),
        apiGet('/email/notification-config'),
      ]);
      if (settingsRes.success && settingsRes.data) {
        const settingsObj = {};
        settingsRes.data.forEach(s => {
          settingsObj[s.setting_key] = s.setting_value;
        });
        setSettings(settingsObj);
      }
      if (typesRes.success && Array.isArray(typesRes.data)) {
        setNotificationTypes(typesRes.data);
      }
      if (configRes.success && Array.isArray(configRes.data)) {
        const cfg = {};
        configRes.data.forEach(c => { cfg[c.notification_type] = c; });
        setNotificationConfig(cfg);
      }
    } catch (error) {
      logger.error('Error loading email settings', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: 'Failed to load email settings' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Group notification types by category for display (shared helper, keeps parity
  // with the Recipients dialog).
  const groupedTypes = groupByCategory(notificationTypes);

  // Collapsible category sections — all collapsed by default once types load.
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  useEffect(() => {
    if (groupedTypes.length) {
      setCollapsedCategories(new Set(groupedTypes.map(g => g.category)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationTypes]);

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category); else next.add(category);
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const trimmedSettings = Object.fromEntries(
        Object.entries(settings).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
      );
      await apiPost('/email/settings/bulk', { settings: trimmedSettings });
      setSettings(trimmedSettings);
      setMessage({ type: 'success', text: 'Email settings saved successfully' });
    } catch (error) {
      logger.error('Error saving email settings', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: extractApiError(error, 'Failed to save settings') });
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleToggleChange = (key) => (event) => {
    handleSettingChange(key, event.target.checked ? 'true' : 'false');
  };

  // Update a scheduled type's cadence and persist immediately (PUT /notification-config/:type).
  const handleCadenceChange = async (type, field, value) => {
    const current = notificationConfig[type] || { email_frequency: 'daily', email_day_of_week: 1, email_day_of_month: 1 };
    const next = { ...current, [field]: value };
    setNotificationConfig(prev => ({ ...prev, [type]: next }));
    try {
      await apiPut(`/email/notification-config/${type}`, {
        email_frequency: next.email_frequency,
        email_day_of_week: next.email_day_of_week,
        email_day_of_month: next.email_day_of_month,
      });
      setMessage({ type: 'success', text: 'Notification cadence updated' });
    } catch (error) {
      logger.error('Error updating notification cadence', { message: error.message });
      setMessage({ type: 'error', text: extractApiError(error, 'Failed to update cadence') });
      // Reload to resync from the server on failure.
      loadSettings();
    }
  };

  // Changing the SMTP port auto-syncs SSL/TLS to the only valid pairing: implicit
  // TLS on 465, STARTTLS (off) on 25/587/2525. Keeps the toggle honest and avoids
  // saving the combo that produces a silent socket error. The admin can still flip
  // it; the API rejects an invalid pairing on save.
  const handlePortChange = (value) => {
    setSettings(prev => ({
      ...prev,
      smtp_port: value,
      smtp_secure: parseInt(value, 10) === 465 ? 'true' : 'false',
    }));
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) return;

    try {
      setSendingTest(true);
      await apiPost('/email/test', { recipientEmail: testEmail });
      setMessage({ type: 'success', text: `Test email sent to ${testEmail}` });
      setTestDialogOpen(false);
      setTestEmail('');
    } catch (error) {
      logger.error('Error sending test email', { message: error.message, status: error.response?.status });
      // Surface the API's own (enriched) message rather than the generic Axios
      // "Request failed with status code 502" so blocked-port hints reach the admin.
      setMessage({ type: 'error', text: extractApiError(error, 'Failed to send test email') });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Email Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure SMTP settings and notification preferences for internal and external recipients
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<SendIcon />}
            onClick={() => setTestDialogOpen(true)}
            disabled={saving}
          >
            Test Email
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadSettings}
            disabled={saving}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      {/* Master Switch */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EmailIcon color={settings.email_enabled === 'true' ? 'primary' : 'disabled'} sx={{ fontSize: 40 }} />
          <Box sx={{ flexGrow: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email_enabled === 'true'}
                  onChange={handleToggleChange('email_enabled')}
                  color="primary"
                  size="medium"
                />
              }
              label={
                <Typography variant="h6">
                  Enable Email Notifications
                </Typography>
              }
            />
            <Typography variant="body2" color="text.secondary">
              Master switch - when disabled, no emails will be sent
            </Typography>
          </Box>
          <Chip
            label={settings.email_enabled === 'true' ? 'ENABLED' : 'DISABLED'}
            color={settings.email_enabled === 'true' ? 'success' : 'default'}
          />
        </Box>
      </Paper>
      {/* SMTP Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          SMTP Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure the mail server used to send notifications
        </Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="SMTP Host"
              value={settings.smtp_host || ''}
              onChange={(e) => handleSettingChange('smtp_host', e.target.value)}
              helperText="e.g., smtp.sendgrid.net (recommended on Azure)"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <TextField
              fullWidth
              label="SMTP Port"
              value={settings.smtp_port || ''}
              onChange={(e) => handlePortChange(e.target.value)}
              helperText="2525 on Azure (25/587 blocked); 465 for SSL"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.smtp_secure === 'true'}
                  onChange={handleToggleChange('smtp_secure')}
                />
              }
              label="Use SSL/TLS"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Off for ports 2525/587 (STARTTLS); on only for 465
            </Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="SMTP Username"
              value={settings.smtp_user || ''}
              onChange={(e) => handleSettingChange('smtp_user', e.target.value)}
              helperText="For SendGrid, use the literal value: apikey"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="SMTP Password"
              type="password"
              value={settings.smtp_password || ''}
              onChange={(e) => handleSettingChange('smtp_password', e.target.value)}
              helperText="For SendGrid, paste your API key. Leave masked (••••) to keep the saved value."
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="From Email"
              value={settings.smtp_from_email || ''}
              onChange={(e) => handleSettingChange('smtp_from_email', e.target.value)}
              helperText="The email address notifications are sent from"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="From Name"
              value={settings.smtp_from_name || ''}
              onChange={(e) => handleSettingChange('smtp_from_name', e.target.value)}
              helperText="Display name for sent emails"
            />
          </Grid>
        </Grid>
      </Paper>
      {/* Notification Type Toggles */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Notification Types
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Control which notifications are sent to internal staff. All notifications are internal-only —
          investors and brokers receive statements only (a separate feature), not these notifications.
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Notification Type</TableCell>
                <TableCell align="center">Send to Internal Staff</TableCell>
                <TableCell align="center">Frequency (scheduled digests)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedTypes.map((group) => {
                const isCollapsed = collapsedCategories.has(group.category);
                return (
                <React.Fragment key={group.category}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => toggleCategory(group.category)}
                  >
                    <TableCell colSpan={3} sx={{ bgcolor: 'action.hover', fontWeight: 600 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isCollapsed ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                        {group.label}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {!isCollapsed && group.items.map((notif) => {
                    const cfg = notificationConfig[notif.key] || {};
                    const freq = cfg.email_frequency || 'daily';
                    const isScheduled = SCHEDULED_TYPES.has(notif.key);
                    return (
                      <TableRow key={notif.key}>
                        <TableCell>{notif.label}</TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={settings[`internal_${notif.key}`] === 'true'}
                            onChange={handleToggleChange(`internal_${notif.key}`)}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {isScheduled ? (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                              <Select
                                size="small"
                                value={freq}
                                onChange={(e) => handleCadenceChange(notif.key, 'email_frequency', e.target.value)}
                                sx={{ minWidth: 110 }}
                              >
                                {FREQUENCY_OPTIONS.map(f => (
                                  <MenuItem key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</MenuItem>
                                ))}
                              </Select>
                              {freq === 'weekly' && (
                                <Select
                                  size="small"
                                  value={cfg.email_day_of_week ?? 1}
                                  onChange={(e) => handleCadenceChange(notif.key, 'email_day_of_week', e.target.value)}
                                  sx={{ minWidth: 120 }}
                                >
                                  {DAY_OF_WEEK_LABELS.map((d, i) => (
                                    <MenuItem key={i} value={i}>{d}</MenuItem>
                                  ))}
                                </Select>
                              )}
                              {freq === 'monthly' && (
                                <Select
                                  size="small"
                                  value={cfg.email_day_of_month ?? 1}
                                  onChange={(e) => handleCadenceChange(notif.key, 'email_day_of_month', e.target.value)}
                                  sx={{ minWidth: 90 }}
                                >
                                  {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                    <MenuItem key={d} value={d}>Day {d}</MenuItem>
                                  ))}
                                </Select>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Instant</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={loadSettings} disabled={saving}>
          Reset
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a test email to verify your SMTP configuration is working correctly.
          </Typography>
          <TextField
            fullWidth
            label="Recipient Email"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="your.email@example.com"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)} disabled={sendingTest}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendTestEmail}
            disabled={!testEmail || sendingTest}
          >
            {sendingTest ? 'Sending...' : 'Send Test Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmailSettingsPage;
